import { apiService } from './ApiService';
import { AxiosResponse } from 'axios';

export interface DiscountAgreement {
  id: number;
  organization_id?: number;
  customer_name: string;
  customer_type: 'b2b' | 'b2c';
  scope_type: 'brand' | 'category' | 'part_name' | 'oe_sku' | 'supplier_sku';
  scope_value: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  currency: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  is_active: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DiscountAgreementCreate {
  customer_name: string;
  customer_type: 'b2b' | 'b2c';
  scope_type: 'brand' | 'category' | 'part_name' | 'oe_sku' | 'supplier_sku';
  scope_value: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  currency?: string | null;
  effective_date?: string | null;
  expiry_date?: string | null;
}

export interface DiscountAgreementUpdate extends Partial<DiscountAgreementCreate> {
  is_active?: boolean;
}

export interface DiscountSummary {
  total_active: number;
  customers_covered: number;
  b2b_default_discount: number | null;
  b2b_default_discount_type: string | null;
  b2b_default_currency: string | null;
}

export interface B2BDefault {
  b2b_default_discount_type: string | null;
  b2b_default_discount_value: number | null;
  b2b_default_currency: string | null;
}

export interface AuditLogEntry {
  id: number;
  timestamp: string;
  actor_user_id: string;
  actor_display_name: string | null;
  entity_type: string;
  entity_id: string;
  action: 'created' | 'updated' | 'deleted';
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
}

export interface PriceResolveRequest {
  customer_name: string;
  retail_price: number;
  currency: string;
  part_brand?: string;
  part_category?: string;
  part_name?: string;
  oe_sku?: string;
  supplier_sku?: string;
}

export interface PriceResolveResult {
  retail_price: number;
  currency: string;
  discount_type: string | null;
  discount_value: number;
  scope_type: string | null;
  scope_value: string | null;
  discount_amount: number;
  final_price: number;
  agreement_id: number | null;
}

export interface OrderLineItemRequest {
  line_id: string;
  description?: string;
  retail_price: number;
  quantity: number;
  part_brand?: string;
  part_category?: string;
  part_name?: string;
  oe_sku?: string;
  supplier_sku?: string;
}

export interface OrderLineItemResult extends OrderLineItemRequest {
  discount_type: string | null;
  discount_value: number;
  scope_type: string | null;
  scope_value: string | null;
  discount_amount: number;
  final_price: number;
  line_total: number;
  agreement_id: number | null;
}

export interface OrderPriceResolveRequest {
  customer_name: string;
  currency: string;
  items: OrderLineItemRequest[];
}

export interface OrderPriceResolveResult {
  customer_name: string;
  currency: string;
  items: OrderLineItemResult[];
  retail_total: number;
  discounted_total: number;
  total_discount_amount: number;
  has_discount: boolean;
}

export class DiscountService {
  private static instance: DiscountService;

  public static getInstance(): DiscountService {
    if (!DiscountService.instance) {
      DiscountService.instance = new DiscountService();
    }
    return DiscountService.instance;
  }

  public async listAgreements(params?: {
    customer_name?: string;
    scope_type?: string;
    is_active?: boolean;
  }): Promise<AxiosResponse<{ agreements: DiscountAgreement[] }>> {
    const query = new URLSearchParams();
    if (params?.customer_name) query.set('customer_name', params.customer_name);
    if (params?.scope_type) query.set('scope_type', params.scope_type);
    if (params?.is_active !== undefined) query.set('is_active', String(params.is_active));
    const qs = query.toString();
    return apiService.get(`/api/discounts${qs ? '?' + qs : ''}`);
  }

  public async createAgreement(data: DiscountAgreementCreate): Promise<AxiosResponse<DiscountAgreement>> {
    return apiService.post('/api/discounts', data);
  }

  public async updateAgreement(id: number, data: DiscountAgreementUpdate): Promise<AxiosResponse<DiscountAgreement>> {
    return apiService.put(`/api/discounts/${id}`, data);
  }

  public async deleteAgreement(id: number): Promise<AxiosResponse<{ deleted: boolean }>> {
    return apiService.delete(`/api/discounts/${id}`);
  }

  public async getSummary(): Promise<AxiosResponse<DiscountSummary>> {
    return apiService.get('/api/discounts/summary');
  }

  public async getAuditTrail(params?: {
    actor_user_id?: string;
    action?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<AxiosResponse<{ logs: AuditLogEntry[] }>> {
    const query = new URLSearchParams();
    if (params?.actor_user_id) query.set('actor_user_id', params.actor_user_id);
    if (params?.action) query.set('action', params.action);
    if (params?.from_date) query.set('from_date', params.from_date);
    if (params?.to_date) query.set('to_date', params.to_date);
    const qs = query.toString();
    return apiService.get(`/api/discounts/audit${qs ? '?' + qs : ''}`);
  }

  public async resolvePrice(data: PriceResolveRequest): Promise<AxiosResponse<PriceResolveResult>> {
    return apiService.post('/api/discounts/resolve-price', data);
  }

  public async resolveOrderPricing(data: OrderPriceResolveRequest): Promise<AxiosResponse<OrderPriceResolveResult>> {
    return apiService.post('/api/discounts/resolve-order', data);
  }

  public async resolveAndPersistOrderPricing(orderId: number, data: OrderPriceResolveRequest): Promise<AxiosResponse<OrderPriceResolveResult>> {
    return apiService.post(`/api/discounts/resolve-order/${orderId}/persist`, data);
  }

  public async getB2BDefault(): Promise<AxiosResponse<B2BDefault>> {
    return apiService.get('/api/discounts/b2b-default');
  }

  public async setB2BDefault(data: {
    b2b_default_discount_type: string;
    b2b_default_discount_value: number;
    b2b_default_currency?: string;
  }): Promise<AxiosResponse<B2BDefault>> {
    return apiService.put('/api/discounts/b2b-default', data);
  }
}

export const discountService = DiscountService.getInstance();
